import * as React from 'react'
import { ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Modal from '@components/modal/Modal'
import Icon from '@components/icon/Icons'
import Button from '@components/button/Button'
import TextInput from '@components/textInput/TextInput'
import Drawer from '@components/drawer/Drawer'
import Choice, { Option } from '@components/choice/Choice'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import theme from '@theme/Theme'

interface Props {
  submitStatus: Status
  onSubmit: (response: Omit<AcceptIncidentRequest, 'utilisateurId'>) => void
}

const RefuseForm = ({ submitStatus, onSubmit }: Props) => {
  const i18n = useI18n()
  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const REASONS: Option[] = [
    { value: 'wrongProvider', label: i18n.t(`screens.incidentDetail.refuseForm.wrongProvider`) },
    { value: 'other', label: i18n.t(`screens.incidentDetail.refuseForm.other`) },
  ]

  const [reason, setReason] = React.useState<Option>(REASONS[0])
  const [commentaire, setCommentaire] = React.useState('')

  const buttonStatus =
    reason.value === 'other' && !commentaire ? 'disabled' : submitStatus === 'loading' ? 'loading' : 'active'

  const renderForm = () => (
    <>
      <Title>{i18n.t('screens.incidentDetail.refuseForm.reason')}</Title>
      <ChoiceContainer>
        <Choice data={REASONS} onItemSelected={setReason} numColumns={2} value={reason} />
      </ChoiceContainer>
      <Title>{i18n.t('screens.incidentDetail.refuseForm.comment')}</Title>
      <TextInput
        value={commentaire}
        onChange={setCommentaire}
        numberOfLines={4}
        placeholder={i18n.t('screens.incidentDetail.commentPlaceholder')}
      />
    </>
  )

  const submit = () => {
    onSubmit({ accept: false, isMauvaisPrestataire: reason.value === 'wrongProvider', commentaire })
    if (isPhone) {
      Drawer.close()
    } else {
      Modal.close()
    }
  }

  if (isPhone) {
    return (
      <Container>
        <Title isCentered>{i18n.t('screens.incidentDetail.buttons.refuse')}</Title>
        <CloseButton activeOpacity={0.9} onPress={Drawer.close}>
          <Icon name="close" size={24} color={theme.colors.primary} />
        </CloseButton>
        {renderForm()}
        <ButtonContainer width={width}>
          <Button status={buttonStatus} label={i18n.t('common.send')} onClick={submit} />
        </ButtonContainer>
      </Container>
    )
  }

  return (
    <ModalContainer>
      <ModalHeader>{i18n.t('screens.incidentDetail.buttons.refuse')}</ModalHeader>
      <CloseButton activeOpacity={0.9} onPress={() => Modal.close()}>
        <Icon name="close" size={24} color={theme.colors.primary} />
      </CloseButton>

      <FormContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
        {renderForm()}
      </FormContainer>
      <ModalButton>
        <Button status={buttonStatus} label={i18n.t('common.send')} onClick={submit} />
      </ModalButton>
    </ModalContainer>
  )
}

export default RefuseForm

const Container = styled(View)`
  padding: 0 16px 16px;
  flex: 1;
`
const Title = styled(Text)<{ isCentered?: boolean }>`
  margin-bottom: 24px;
  ${(props) => props.theme.fonts.Headline};
  ${(props) => props.isCentered && 'text-align:center;'}
`
const ButtonContainer = styled(View)<{ width: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 16px;
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background};
`
const CloseButton = styled(TouchableOpacity)`
  padding: 16px;
  position: absolute;
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
      top: -18px;
      left: 0px;`
      : `
      top: 0px;
      right: 0px;`};
  z-index: 1;
`
const ChoiceContainer = styled(View)`
  margin-bottom: 24px;
`
const ModalContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.incident};
  width: 650px;
  height: 500px;
`
const FormContainer = styled(ScrollView)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 35px 24px;
`
const ModalHeader = styled(Text)`
  padding: 24px 24px 36px;
  ${(props) => props.theme.fonts.Title3};
`
const ModalButton = styled(View)`
  padding: 24px;
  width: 280px;
  align-self: center;
`
